// src/hooks/useOpportunityData.js
/**
 * Custom hook for processing opportunity data
 *
 * This hook processes embedded opportunity data (no API calls needed)
 * Follows the same pattern as useClientData and useProjectData
 *
 * @param {Object} opportunity - The raw opportunity object from search
 * @returns {Object} Processed opportunity data with loading states
 */
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { apiFetch } from '../utils/api';
import { Logger } from '../utils/logger';

const logger = new Logger('useOpportunityData');

/**
 * Process raw opportunity data into standardized format
 * All data comes from embedded fields - no API calls required
 */
const processOpportunityData = (opportunity) => {
  if (!opportunity) return null;

  return {
    // Basic information with fallbacks
    name: opportunity.name || 'Unknown Opportunity',
    opportunity_id: opportunity.opportunity_id || `${Date.now()}`,
    client_name: opportunity.client_name || 'Unknown Client',

    // Format and normalize status
    stage: opportunity.stage || 'Not Started',

    // Dates with standardized format
    expected_close_date: opportunity.expected_close_date || null,
    start_date: opportunity.start_date || null,

    // Financial information
    expected_value: opportunity.expected_value || 0,
    win_probability: opportunity.win_probability || 0,

    // Enhanced details from embedded data
    industry: opportunity.client_industry || opportunity.industry || null,
    required_headcount: opportunity.required_headcount || 0,
    project_duration: opportunity.project_duration || 0,
    description: opportunity.description || null,

    // Team composition from embedded data
    team_size: Array.isArray(opportunity.team) ? opportunity.team.length : 0,
    technical_resources_count: Array.isArray(opportunity.technical_resources) ? opportunity.technical_resources.length : 0,
    pursuit_team_size: opportunity.pursuit_team_size || 0,
    sales_owner: opportunity.sales_owner || null,

    // Revi score for primary contact
    revi_score: opportunity.revi_score || null,

    // Statement of Work
    has_sow: opportunity.has_sow || false,
    sow_url: opportunity.sow_url || null,

    // Team members (use embedded data directly)
    team: Array.isArray(opportunity.team) ? opportunity.team : [],

    // Technical resources
    technical_resources: Array.isArray(opportunity.technical_resources) ? opportunity.technical_resources : [],

    // Roles data
    roles: Array.isArray(opportunity.roles) ? opportunity.roles : [],

    // Contacts (preserve embedded data)
    contacts: Array.isArray(opportunity.contacts) ? opportunity.contacts : []
  };
};

/**
 * Process contact data from opportunity
 */
const processContactData = (opportunity, displayOpportunity) => {
  if (!opportunity?.contacts || !Array.isArray(opportunity.contacts)) {
    return {
      primaryContact: null,
      contacts: []
    };
  }

  // Find primary contact from embedded data
  const primaryContactData = opportunity.contacts.find(contact => contact.is_primary) || opportunity.contacts[0];

  return {
    primaryContact: primaryContactData ? {
      ...primaryContactData,
      revi_score: displayOpportunity?.revi_score || null
    } : null,
    contacts: opportunity.contacts
  };
};

export const useOpportunityData = (opportunity) => {
  const abortControllerRef = useRef(null);

  // Consolidated state
  const [state, setState] = useState({
    displayOpportunity: {},
    primaryContact: null,
    contacts: [],
    team: [],
    hasSow: false,
    loading: { opportunity: false, contact: false, team: false },
    errors: { opportunity: null, contact: null, team: null }
  });

  const processedOpportunity = useMemo(() => {
    return processOpportunityData(opportunity);
  }, [opportunity]);

  const applyProcessed = useCallback((opp, processed) => {
    const { primaryContact, contacts } = processContactData(opp, processed);
    setState({
      displayOpportunity: processed,
      primaryContact,
      contacts,
      team: processed.team,
      hasSow: processed.has_sow,
      loading: { opportunity: false, contact: false, team: false },
      errors: { opportunity: null, contact: null, team: null }
    });
  }, []);

  useEffect(() => {
    if (!opportunity) return;

    const processed = processedOpportunity;
    if (!processed) return;

    // Determine if the object is minimal (only id+name, no embedded team/contacts)
    const hasEmbeddedData = (
      Array.isArray(opportunity.team) ||
      Array.isArray(opportunity.contacts) ||
      opportunity.stage ||
      opportunity.expected_value
    );

    if (hasEmbeddedData) {
      applyProcessed(opportunity, processed);
      return;
    }

    // Minimal object — fetch full data from API
    const oppId = opportunity.opportunity_id;
    const idStr = oppId?.toString() || '';
    if (!idStr || idStr.length > 15) {
      applyProcessed(opportunity, processed);
      return;
    }

    setState(prev => ({ ...prev, loading: { opportunity: true, contact: true, team: true } }));
    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;

    apiFetch(`opportunities/${oppId}/full`, { signal })
      .then(r => r.ok ? r.json() : null)
      .then(fullData => {
        if (!fullData) return;
        const merged = { ...opportunity, ...fullData };
        applyProcessed(merged, processOpportunityData(merged));
      })
      .catch(err => {
        if (err.name !== 'AbortError') {
          logger.error('Error fetching full opportunity data:', err);
          applyProcessed(opportunity, processed);
        }
      });

    return () => { if (abortControllerRef.current) abortControllerRef.current.abort(); };
  }, [opportunity, processedOpportunity, applyProcessed]);

  return state;
};
